import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import { Box, InputAdornment, SxProps, TextField, Theme, Typography } from '@mui/material';
import { SystemStyleObject } from '@mui/system';

const STRINGS = appStrings.modules.satActBooster.scoreField;

type ScoreFieldProps = Readonly<{
  id: string;
  label: string;
  value: number | null;
  min: number;
  max: number;
  onChange: (value: number | null) => void;
  description?: string;
  /** Trailing unit, e.g. "%". */
  suffix?: string;
  /** In-field label segment with a divider; replaces the above-field label. */
  leadingLabel?: string;
  /** With `leadingLabel`, let the label fill the field and keep a fixed, narrow value segment. */
  fillLabel?: boolean;
  optional?: boolean;
  /** Shown under the input when the value is valid, e.g. "1–36". */
  rangeHint?: string;
  /** External validation error; the built-in range error takes precedence. */
  errorText?: string;
  /** Soft (non-error) warning under the input; shown only when there is no error. */
  warningText?: string;
}>;

const parseScore = (raw: string): number | null => {
  if (raw.trim() === '') return null;

  const parsed = Number(raw);

  return Number.isNaN(parsed) ? null : parsed;
};

/** Compact numeric score input with inline out-of-range validation. */
export const ScoreField = ({
  id,
  label,
  value,
  min,
  max,
  onChange,
  description,
  suffix,
  leadingLabel,
  fillLabel,
  optional,
  rangeHint,
  errorText,
  warningText,
}: ScoreFieldProps) => {
  const isOutOfRange = value !== null && (value < min || value > max);
  const isError = isOutOfRange || errorText !== undefined;
  // A warning never overrides an error, and gives way to the range hint when absent.
  const showWarning = !isError && warningText !== undefined;
  const optionalHint = optional ? STRINGS.optional : undefined;
  const showOptionalHint =
    !isError && !showWarning && rangeHint === undefined && optionalHint !== undefined;

  const restingHint =
    rangeHint && optionalHint ? `${rangeHint} · ${optionalHint}` : (rangeHint ?? optionalHint);

  const helperText = isOutOfRange
    ? STRINGS.outOfRange(min, max)
    : (errorText ?? (showWarning ? warningText : restingHint));

  // Inline-label variant: a two-segment control built as real flex boxes.
  // With `fillLabel` the label segment fills and the value stays narrow (subscores);
  // otherwise the label is content-sized and the value segment fills (section scores).
  // Either way both segments share even 10px vertical padding and stretch to equal
  // height, so a wrapped label grows the whole box without cramping.
  if (leadingLabel) {
    const fill = Boolean(fillLabel);
    const helperColorSx = isError
      ? segmentErrorTextSx
      : showWarning
        ? segmentWarningTextSx
        : segmentHelperTextSx;

    return (
      <Box sx={fieldSx}>
        <Box sx={[segmentedRootSx, isError && segmentedRootErrorSx]}>
          <Box sx={(theme) => segmentLabel(theme, fill)}>
            <Typography variant="text-sm-regular" sx={segmentLabelTextSx}>
              {leadingLabel}
            </Typography>
          </Box>
          <Box sx={(theme) => segmentValue(theme, fill)}>
            <Box
              component="input"
              id={id}
              type="number"
              aria-label={label}
              aria-invalid={isError || undefined}
              aria-describedby={`${id}-helper`}
              value={value ?? ''}
              onChange={(event) => onChange(parseScore(event.target.value))}
              min={min}
              max={max}
              sx={(theme) => segmentInput(theme, fill)}
            />
            {suffix && (
              <Typography variant="text-sm-regular" sx={segmentSuffixSx}>
                {suffix}
              </Typography>
            )}
          </Box>
        </Box>
        {/* Always rendered so a message appearing mid-typing can't reflow the surrounding grid. */}
        <Typography id={`${id}-helper`} variant="text-xs-regular" sx={helperColorSx}>
          {helperText || '\u00a0'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={fieldSx}>
      <Box sx={labelRowSx}>
        <Typography variant="text-sm-semibold" sx={labelSx}>
          {label}
        </Typography>
        {optional && (
          <Typography variant="text-xs-regular" sx={optionalSx}>
            {STRINGS.optional}
          </Typography>
        )}
      </Box>
      {description && (
        <Typography variant="text-xs-regular" sx={descriptionSx}>
          {description}
        </Typography>
      )}
      <TextField
        id={id}
        type="number"
        placeholder={STRINGS.emptyPlaceholder}
        value={value ?? ''}
        onChange={(event) => onChange(parseScore(event.target.value))}
        error={isError}
        helperText={helperText}
        inputProps={{ min, max, 'aria-label': label }}
        InputProps={{
          endAdornment: suffix ? (
            <InputAdornment position="end">{suffix}</InputAdornment>
          ) : undefined,
        }}
        FormHelperTextProps={
          showWarning
            ? { sx: warningHelperSx }
            : showOptionalHint
              ? { sx: optionalHelperSx }
              : undefined
        }
        sx={inputSx}
      />
    </Box>
  );
};

const fieldSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xxs'],
});

const labelRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'baseline',
  gap: backrs.spacing['spacing-xs'],
});

const labelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const optionalSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-quaternary'],
});

const descriptionSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const inputSx: SxProps<Theme> = {
  maxWidth: '140px',
};

const warningHelperSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-warning-primary'],
});

const optionalHelperSx: SxProps<Theme> = ({ backrs }) => ({
  marginLeft: 0,
  marginRight: 0,
  textAlign: 'left',
  color: backrs.colors['Colors/Text/text-quaternary'],
});

// Two-segment inline-label control. Both segments share even 10px vertical
// padding and stretch to equal height, so a wrapped label grows the whole box cleanly.
// `fill` = fillLabel: true → label fills, value narrow (subscores); false → label
// content-sized, value fills (section scores).
const segmentedRootSx = ({ backrs }: Theme): SystemStyleObject<Theme> => ({
  display: 'flex',
  alignItems: 'stretch',
  width: '100%',
  overflow: 'hidden',
  borderRadius: backrs.radius['radius-md'],
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  // The inner input clears its own outline, so the ring has to live on the wrapper.
  '&:focus-within': {
    outline: `2px solid ${backrs.colors['Colors/Foreground/fg-brand-primary']}`,
    outlineOffset: '-1px',
  },
});

const segmentedRootErrorSx = ({ backrs }: Theme): SystemStyleObject<Theme> => ({
  borderColor: backrs.colors['Colors/Border/border-error'],
});

const segmentLabel = ({ backrs }: Theme, fill: boolean): SystemStyleObject<Theme> => ({
  flex: fill ? '1 1 0' : '0 0 auto',
  minWidth: fill ? 0 : 'auto',
  display: 'flex',
  alignItems: 'center',
  // No matching spacing tokens for this padding.
  padding: '10px 12px 10px 14px',
  borderRight: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
});

const segmentLabelTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
  lineHeight: 1.4,
});

const segmentValue = ({ backrs }: Theme, fill: boolean): SystemStyleObject<Theme> => ({
  flex: fill ? '0 0 auto' : '1 1 0',
  minWidth: fill ? 'auto' : 0,
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
  // No matching spacing tokens for this padding.
  padding: '10px 14px',
});

const segmentInput = ({ backrs, typography }: Theme, fill: boolean): SystemStyleObject<Theme> => ({
  ...typography['text-sm-regular'],
  width: fill ? '30px' : '100%',
  flex: fill ? '0 0 auto' : '1 1 0',
  minWidth: 0,
  border: 'none',
  outline: 'none',
  padding: 0,
  margin: 0,
  backgroundColor: 'transparent',
  textAlign: fill ? 'right' : 'left',
  color: backrs.colors['Colors/Text/text-primary'],
  MozAppearance: 'textfield',
  '&::placeholder': {
    color: backrs.colors['Colors/Text/text-placeholder'],
    opacity: 1,
  },
  '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
    WebkitAppearance: 'none',
    margin: 0,
  },
});

const segmentSuffixSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const segmentHelperTextSx: SxProps<Theme> = ({ backrs }) => ({
  marginLeft: 0,
  textAlign: 'left',
  color: backrs.colors['Colors/Text/text-quaternary'],
});

const segmentErrorTextSx: SxProps<Theme> = ({ backrs }) => ({
  marginLeft: 0,
  textAlign: 'left',
  color: backrs.colors['Colors/Text/text-error-primary'],
});

const segmentWarningTextSx: SxProps<Theme> = ({ backrs }) => ({
  marginLeft: 0,
  textAlign: 'left',
  color: backrs.colors['Colors/Text/text-warning-primary'],
});
